import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, ArrowUp, ArrowDown, Plus, Trash2, ImagePlus, Lock, Check, Sparkles, Wand2, Bell, Printer, Volume2, Camera } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { IconRenderer } from "../lib/icons";
import { fileToDataUrl } from "../lib/image";
import { STEP_SUGGESTIONS } from "../lib/stepSuggestions";
import { playJingle, speak } from "../lib/speech";
import Header from "../components/Header";
import Modal from "../components/Modal";
import IconPicker from "../components/IconPicker";

const SOUND_OPTIONS = [
  { id: "chime", label: "Gentle chime" },
  { id: "lullaby", label: "Lullaby" },
  { id: "fun", label: "Funny bounce" },
  { id: "bell", label: "Bell" },
];

export default function RoutineBuilder() {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const navigate = useNavigate();
  const { canUsePro } = useAuth();
  const [name, setName] = useState("");
  const [icon, setIcon] = useState("ListChecks");
  const [steps, setSteps] = useState([]);
  const [alarmTime, setAlarmTime] = useState("");
  const [alarmEnabled, setAlarmEnabled] = useState(false);
  const [alarmSound, setAlarmSound] = useState("chime");
  const [alarmMessage, setAlarmMessage] = useState("");
  const [profileId, setProfileId] = useState("");
  const [iconPickerFor, setIconPickerFor] = useState(null); // null | "routine" | step index
  const [saving, setSaving] = useState(false);
  const [showAiModal, setShowAiModal] = useState(false);
  const [aiDesc, setAiDesc] = useState("");
  const [aiBusy, setAiBusy] = useState(false);
  const [aiImgFor, setAiImgFor] = useState(null);
  const [photoBusy, setPhotoBusy] = useState(false);
  const photoRoutineInput = useRef(null);
  const fileInputs = useRef({});

  useEffect(() => {
    (async () => {
      try {
        const { data: profiles } = await api.get("/profiles");
        const pid = localStorage.getItem("rb_profile");
        setProfileId(profiles.find((p) => p.id === pid)?.id || profiles[0]?.id || "");
        if (isEdit) {
          const { data } = await api.get(`/routines/${id}`);
          setName(data.name);
          setIcon(data.icon);
          setSteps(data.steps);
          setProfileId(data.profile_id);
          setAlarmTime(data.alarm_time || "");
          setAlarmEnabled(Boolean(data.alarm_enabled));
          setAlarmSound(data.alarm_sound || "chime");
          setAlarmMessage(data.alarm_message || "");
        }
      } catch (e) {
        toast.error(apiError(e));
      }
    })();
  }, [id, isEdit]);

  const updateStep = (i, patch) => setSteps((s) => s.map((st, idx) => (idx === i ? { ...st, ...patch } : st)));
  const removeStep = (i) => setSteps((s) => s.filter((_, idx) => idx !== i));
  const moveStep = (i, dir) =>
    setSteps((s) => {
      const j = i + dir;
      if (j < 0 || j >= s.length) return s;
      const copy = [...s];
      [copy[i], copy[j]] = [copy[j], copy[i]];
      return copy;
    });
  const addStep = () =>
    setSteps((s) => [...s, { id: `tmp-${Date.now()}`, label: "", icon: "Star", image: null }]);

  const addSuggestion = (item) =>
    setSteps((s) => [...s, { id: `tmp-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`, label: item.label, icon: item.icon, image: null }]);

  const hasStep = (label) => steps.some((s) => s.label.trim().toLowerCase() === label.toLowerCase());

  const generateRoutine = async () => {
    if (!aiDesc.trim()) return toast.error("Describe the routine first");
    setAiBusy(true);
    try {
      const { data } = await api.post("/ai/routine", { description: aiDesc.trim() });
      setName(data.name);
      setIcon(data.icon);
      setSteps(data.steps);
      setShowAiModal(false);
      toast.success("Routine generated — review and edit it below");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setAiBusy(false);
    }
  };

  const generateStepImage = async (i) => {
    const label = steps[i]?.label?.trim();
    if (!label) return toast.error("Give the step a label first");
    setAiImgFor(i);
    try {
      const { data } = await api.post("/ai/image", { label });
      updateStep(i, { image: data.image });
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setAiImgFor(null);
    }
  };

  const routineFromPhoto = async (file) => {
    if (!file) return;
    setPhotoBusy(true);
    try {
      const dataUrl = await fileToDataUrl(file, 512);
      const { data } = await api.post("/ai/routine-from-photo", { image: dataUrl });
      if (!name.trim()) setName(data.name);
      setIcon(data.icon);
      setSteps(data.steps);
      toast.success("Steps created from your photo — review and edit below");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setPhotoBusy(false);
      if (photoRoutineInput.current) photoRoutineInput.current.value = "";
    }
  };

  const uploadPhoto = async (i, file) => {
    if (!file) return;
    try {
      const dataUrl = await fileToDataUrl(file, 256);
      updateStep(i, { image: dataUrl });
    } catch {
      toast.error("Could not read that photo");
    }
  };

  const printRoutine = () => {
    const clean = steps.filter((s) => s.label.trim());
    if (!name.trim() || clean.length === 0) return toast.error("Add a name and at least one step first");
    const esc = (s) => s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
    const w = window.open("", "_blank");
    if (!w) return toast.error("Pop-up blocked — allow pop-ups to print");
    w.document.write(`<html><head><title>${esc(name)}</title><style>body{font-family:sans-serif;padding:32px;color:#0A192F}h1{font-size:34px}.step{display:flex;align-items:center;gap:18px;font-size:26px;font-weight:700;margin:20px 0}.box{width:34px;height:34px;border:3px solid #0A192F;border-radius:8px;flex-shrink:0}</style></head><body><h1>${esc(name)}</h1>${clean.map((s, i) => `<div class="step"><div class="box"></div><span>${i + 1}. ${esc(s.label)}</span></div>`).join("")}<p style="margin-top:32px;color:#666">Made with RoutineBuddy</p></body></html>`);
    w.document.close();
    w.print();
  };

  const save = async () => {
    const cleanSteps = steps.filter((s) => s.label.trim());
    if (!name.trim()) return toast.error("Give the routine a name");
    if (cleanSteps.length === 0) return toast.error("Add at least one step");
    setSaving(true);
    try {
      const payload = {
        profile_id: profileId,
        name: name.trim(),
        icon,
        steps: cleanSteps,
        alarm_time: alarmEnabled && alarmTime ? alarmTime : null,
        alarm_enabled: alarmEnabled && !!alarmTime,
        alarm_sound: alarmSound,
        alarm_message: alarmMessage.trim() || null,
      };
      if (isEdit) await api.put(`/routines/${id}`, payload);
      else await api.post("/routines", payload);
      toast.success(isEdit ? "Routine saved" : "Routine created");
      navigate("/app");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="routine-builder" className="min-h-screen bg-background">
      <Header />
      <main className="max-w-3xl mx-auto px-4 py-8 pb-24">
        <button data-testid="back-button" onClick={() => navigate("/app")} className="min-h-[48px] px-4 rounded-full border-2 border-foreground/25 bg-white font-bold flex items-center gap-2 hover:bg-secondary transition-colors duration-200">
          <ArrowLeft size={20} strokeWidth={2.5} /> Back
        </button>

        <h1 className="mt-6 text-3xl font-extrabold tracking-tight">{isEdit ? "Edit routine" : "New routine"}</h1>

        <div className="mt-6 bg-white rounded-3xl border-2 border-foreground/10 p-6 space-y-5">
          <div>
            <label htmlFor="routine-name" className="block text-base font-bold mb-1">Routine name</label>
            <input
              id="routine-name"
              data-testid="routine-name-input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Morning routine"
              className="w-full min-h-[56px] rounded-2xl border-2 border-foreground/25 px-4 text-lg focus:outline-none focus:border-primary"
            />
          </div>
          <div>
            <p className="text-base font-bold mb-2">Routine icon</p>
            <button
              data-testid="routine-icon-button"
              onClick={() => setIconPickerFor("routine")}
              className="min-h-[64px] px-5 rounded-2xl border-2 border-foreground/25 bg-white flex items-center gap-3 font-bold hover:bg-secondary transition-colors duration-200"
            >
              <IconRenderer name={icon} size={32} className="text-primary" /> Change icon
            </button>
          </div>
          <div className="border-t-2 border-border pt-5">
            {canUsePro ? (
              <div className="flex flex-col gap-3">
                <button
                  data-testid="ai-generate-routine-button"
                  onClick={() => setShowAiModal(true)}
                  className="w-full min-h-[64px] rounded-2xl bg-[#9F7AEA] text-white text-lg font-bold flex items-center justify-center gap-2 transition-transform duration-200 ease-out hover:-translate-y-px"
                >
                  <Sparkles size={24} strokeWidth={2.5} /> Generate steps with AI
                </button>
                <input type="file" accept="image/*" capture="environment" className="hidden" ref={photoRoutineInput} onChange={(e) => routineFromPhoto(e.target.files[0])} />
                <button
                  data-testid="photo-routine-button"
                  onClick={() => photoRoutineInput.current?.click()}
                  disabled={photoBusy}
                  className="w-full min-h-[64px] rounded-2xl border-2 border-[#9F7AEA] bg-white text-lg font-bold flex items-center justify-center gap-2 text-[#9F7AEA] transition-colors duration-200 hover:bg-[#9F7AEA]/10 disabled:opacity-60"
                >
                  <Camera size={24} strokeWidth={2.5} /> {photoBusy ? "Reading photo…" : "Snap a photo → make a routine"}
                </button>
              </div>
            ) : (
              <button
                data-testid="ai-generate-locked-button"
                onClick={() => navigate("/premium")}
                className="w-full min-h-[64px] rounded-2xl border-2 border-dashed border-foreground/25 bg-secondary/50 text-lg font-bold flex items-center justify-center gap-2 text-muted-foreground"
              >
                <Lock size={24} strokeWidth={2.5} /> Generate steps with AI (Pro)
              </button>
            )}
          </div>
          <div className="border-t-2 border-border pt-5">
            <p className="text-base font-bold mb-2 flex items-center gap-2"><Bell size={20} strokeWidth={2.5} className="text-primary" /> Routine alarm (optional)</p>
            <div className="flex flex-wrap items-center gap-3">
              <div className="flex items-center gap-2">
                <select
                  data-testid="alarm-hour-select"
                  aria-label="Alarm hour"
                  value={alarmTime ? alarmTime.split(":")[0] : ""}
                  onChange={(e) => {
                    const h = e.target.value;
                    const m = alarmTime ? alarmTime.split(":")[1] : "00";
                    setAlarmTime(h === "" ? "" : `${h}:${m}`);
                  }}
                  className="min-h-[56px] rounded-2xl border-2 border-foreground/25 px-4 text-lg font-bold bg-white focus:outline-none focus:border-primary"
                >
                  <option value="">HH</option>
                  {Array.from({ length: 24 }, (_, h) => String(h).padStart(2, "0")).map((h) => (
                    <option key={h} value={h}>{h}</option>
                  ))}
                </select>
                <span className="text-2xl font-extrabold">:</span>
                <select
                  data-testid="alarm-minute-select"
                  aria-label="Alarm minute"
                  value={alarmTime ? alarmTime.split(":")[1] : ""}
                  onChange={(e) => {
                    const m = e.target.value;
                    const h = alarmTime ? alarmTime.split(":")[0] : "";
                    if (h !== "" && m !== "") setAlarmTime(`${h}:${m}`);
                    else if (m !== "") setAlarmTime(`08:${m}`);
                  }}
                  className="min-h-[56px] rounded-2xl border-2 border-foreground/25 px-4 text-lg font-bold bg-white focus:outline-none focus:border-primary"
                >
                  <option value="">MM</option>
                  {Array.from({ length: 12 }, (_, i) => String(i * 5).padStart(2, "0")).map((m) => (
                    <option key={m} value={m}>{m}</option>
                  ))}
                </select>
              </div>
              <button
                data-testid="alarm-toggle-button"
                onClick={() => setAlarmEnabled((v) => !v)}
                disabled={!alarmTime}
                className={`min-h-[56px] px-5 rounded-full border-2 font-bold transition-colors duration-200 disabled:opacity-50 ${
                  alarmEnabled && alarmTime ? "border-primary bg-primary/10" : "border-foreground/25 bg-white"
                }`}
              >
                {alarmEnabled && alarmTime ? `Alarm on — ${alarmTime}` : "Alarm off"}
              </button>
            </div>
            <p className="mt-2 text-sm text-muted-foreground font-semibold">At this time the app plays a sound and shows a big reminder (while the app is open).</p>

            <p className="mt-4 text-base font-bold">Alarm sound</p>
            <div className="mt-2 flex flex-wrap gap-2">
              {SOUND_OPTIONS.map((s) => (
                <button
                  key={s.id}
                  data-testid={`alarm-sound-${s.id}`}
                  onClick={() => { setAlarmSound(s.id); playJingle(s.id); }}
                  className={`min-h-[52px] px-4 rounded-full border-2 font-bold text-base transition-colors duration-200 ${
                    alarmSound === s.id ? "border-primary bg-primary/10" : "border-foreground/20 bg-white hover:bg-secondary"
                  }`}
                >
                  {s.label}
                </button>
              ))}
            </div>

            <label htmlFor="alarm-message" className="mt-4 block text-base font-bold">What should the app say?</label>
            <div className="mt-2 flex gap-2">
              <input
                id="alarm-message"
                data-testid="alarm-message-input"
                value={alarmMessage}
                onChange={(e) => setAlarmMessage(e.target.value)}
                maxLength={80}
                placeholder={name.trim() ? `Time for ${name.trim()}!` : "e.g. Time to sleep, sleepyhead!"}
                className="flex-1 min-w-0 min-h-[56px] rounded-2xl border-2 border-foreground/25 px-4 text-lg focus:outline-none focus:border-primary"
              />
              <button
                data-testid="alarm-preview-button"
                onClick={() => { playJingle(alarmSound); speak(alarmMessage.trim() || `Time for ${name.trim() || "your routine"}`); }}
                aria-label="Preview alarm"
                title="Preview alarm"
                className="min-w-[56px] min-h-[56px] rounded-2xl border-2 border-foreground/25 bg-white grid place-items-center hover:bg-secondary transition-colors duration-200 shrink-0"
              >
                <Volume2 size={24} strokeWidth={2.5} />
              </button>
            </div>
            <p className="mt-2 text-sm text-muted-foreground font-semibold">Tap a sound to hear it. The speaker button previews the full alarm.</p>
          </div>
        </div>

        <div className="mt-6 bg-white rounded-3xl border-2 border-foreground/10 p-6">
          <h2 className="text-2xl font-extrabold">Quick add</h2>
          <p className="mt-1 text-base text-muted-foreground font-semibold">Tap a suggestion to add it as a step — or type your own below.</p>
          <div data-testid="step-suggestions" className="mt-4 space-y-4">
            {STEP_SUGGESTIONS.map((group) => (
              <div key={group.group}>
                <p className="text-sm font-bold uppercase tracking-wider text-muted-foreground mb-2">{group.group}</p>
                <div className="flex flex-wrap gap-2">
                  {group.items.map((item) => {
                    const added = hasStep(item.label);
                    return (
                      <button
                        key={item.label}
                        data-testid={`suggestion-${item.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                        onClick={() => !added && addSuggestion(item)}
                        disabled={added}
                        className={`min-h-[52px] px-4 rounded-full border-2 font-bold text-base flex items-center gap-2 transition-colors duration-200 ${
                          added
                            ? "border-[#48BB78]/50 bg-[#48BB78]/10 text-[#2F855A]"
                            : "border-foreground/20 bg-white hover:bg-secondary"
                        }`}
                      >
                        {added ? <Check size={20} strokeWidth={3} /> : <IconRenderer name={item.icon} size={20} className="text-primary" />}
                        {item.label}
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="mt-6 space-y-4">
          <h2 className="text-2xl font-extrabold">Steps ({steps.length})</h2>
          {steps.map((step, i) => (
            <div key={step.id} data-testid={`step-row-${i}`} className="bg-white rounded-3xl border-2 border-foreground/10 p-4 flex items-center gap-3">
              <button
                data-testid={`step-icon-button-${i}`}
                onClick={() => setIconPickerFor(i)}
                aria-label={`Change icon for step ${i + 1}`}
                className="w-16 h-16 rounded-2xl border-2 border-foreground/15 grid place-items-center shrink-0 overflow-hidden hover:bg-secondary transition-colors duration-200"
              >
                {step.image ? <img src={step.image} alt="" className="w-full h-full object-cover" /> : <IconRenderer name={step.icon} size={32} className="text-primary" />}
              </button>
              <input
                data-testid={`step-label-input-${i}`}
                value={step.label}
                onChange={(e) => updateStep(i, { label: e.target.value })}
                placeholder={`Step ${i + 1}, e.g. Brush teeth`}
                className="flex-1 min-w-0 min-h-[56px] rounded-2xl border-2 border-foreground/25 px-4 text-lg focus:outline-none focus:border-primary"
              />
              <input
                data-testid={`step-minutes-${i}`}
                type="number"
                min="1"
                max="180"
                value={step.minutes || ""}
                onChange={(e) => updateStep(i, { minutes: e.target.value ? Math.max(1, Math.min(180, Number(e.target.value))) : null })}
                placeholder="min"
                title="Minutes allowed for this step (optional — enables timer alerts)"
                className="w-20 shrink-0 min-h-[56px] rounded-2xl border-2 border-foreground/25 px-2 text-center text-base focus:outline-none focus:border-primary"
              />
              <div className="flex gap-1">
                <button data-testid={`step-up-${i}`} onClick={() => moveStep(i, -1)} aria-label="Move step up" className="min-w-[44px] min-h-[44px] rounded-full grid place-items-center hover:bg-secondary transition-colors duration-200"><ArrowUp size={20} strokeWidth={2.5} /></button>
                <button data-testid={`step-down-${i}`} onClick={() => moveStep(i, 1)} aria-label="Move step down" className="min-w-[44px] min-h-[44px] rounded-full grid place-items-center hover:bg-secondary transition-colors duration-200"><ArrowDown size={20} strokeWidth={2.5} /></button>
                {canUsePro ? (
                  <>
                    <input type="file" accept="image/*" className="hidden" ref={(el) => (fileInputs.current[i] = el)} onChange={(e) => uploadPhoto(i, e.target.files[0])} />
                    <button data-testid={`step-photo-${i}`} onClick={() => fileInputs.current[i]?.click()} aria-label="Add photo to step" className="min-w-[44px] min-h-[44px] rounded-full grid place-items-center hover:bg-secondary transition-colors duration-200"><ImagePlus size={20} strokeWidth={2.5} /></button>
                    <button data-testid={`step-ai-image-${i}`} onClick={() => generateStepImage(i)} disabled={aiImgFor !== null} aria-label="Generate picture with AI" className="min-w-[44px] min-h-[44px] rounded-full grid place-items-center text-[#9F7AEA] hover:bg-[#9F7AEA]/10 transition-colors duration-200 disabled:opacity-50">
                      {aiImgFor === i ? <span className="text-xs font-extrabold animate-pulse">…</span> : <Wand2 size={20} strokeWidth={2.5} />}
                    </button>
                  </>
                ) : (
                  <button data-testid={`step-photo-locked-${i}`} onClick={() => navigate("/premium")} aria-label="Photo steps are a Premium feature" className="min-w-[44px] min-h-[44px] rounded-full grid place-items-center text-muted-foreground hover:bg-secondary transition-colors duration-200"><Lock size={20} strokeWidth={2.5} /></button>
                )}
                <button data-testid={`step-delete-${i}`} onClick={() => removeStep(i)} aria-label="Delete step" className="min-w-[44px] min-h-[44px] rounded-full grid place-items-center text-[#C53030] hover:bg-[#F56565]/10 transition-colors duration-200"><Trash2 size={20} strokeWidth={2.5} /></button>
              </div>
            </div>
          ))}
          <button
            data-testid="add-step-button"
            onClick={addStep}
            className="w-full min-h-[64px] rounded-3xl border-2 border-dashed border-foreground/30 bg-white font-bold text-lg flex items-center justify-center gap-2 hover:bg-secondary transition-colors duration-200"
          >
            <Plus size={24} strokeWidth={3} /> Add a step
          </button>
        </div>

        <div className="mt-8 flex gap-3">
          <button
            data-testid="print-routine-button"
            onClick={printRoutine}
            className="min-h-[64px] px-6 rounded-full border-2 border-foreground/25 bg-white text-lg font-bold flex items-center justify-center gap-2 hover:bg-secondary transition-colors duration-200"
          >
            <Printer size={24} strokeWidth={2.5} /> Print
          </button>
          <button
            data-testid="save-routine-button"
            onClick={save}
            disabled={saving}
            className="flex-1 min-h-[64px] rounded-full bg-primary text-white text-xl font-bold flex items-center justify-center gap-2 transition-transform duration-200 ease-out hover:-translate-y-px disabled:opacity-60"
          >
            <Check size={26} strokeWidth={3} /> {saving ? "Saving…" : "Save routine"}
          </button>
        </div>
      </main>

      {showAiModal && (
        <Modal title="Generate steps with AI" onClose={() => setShowAiModal(false)} testId="ai-routine-modal">
          <label htmlFor="ai-desc" className="block text-base font-bold mb-1">Describe the routine</label>
          <textarea
            id="ai-desc"
            data-testid="ai-routine-description"
            value={aiDesc}
            onChange={(e) => setAiDesc(e.target.value)}
            rows={4}
            placeholder={'e.g. "A calm bedtime routine for an anxious 8-year-old" or "Getting ready for school"'}
            className="w-full rounded-2xl border-2 border-foreground/25 px-4 py-3 text-lg focus:outline-none focus:border-primary"
          />
          <p className="mt-2 text-sm text-muted-foreground font-semibold">AI will create 5–10 simple steps with icons. You can edit everything after.</p>
          <button
            data-testid="ai-routine-generate-button"
            onClick={generateRoutine}
            disabled={aiBusy}
            className="mt-5 w-full min-h-[56px] rounded-full bg-[#9F7AEA] text-white text-lg font-bold flex items-center justify-center gap-2 disabled:opacity-60"
          >
            <Sparkles size={22} strokeWidth={2.5} /> {aiBusy ? "Generating…" : "Generate routine"}
          </button>
        </Modal>
      )}

      {iconPickerFor !== null && (
        <Modal title="Pick an icon" onClose={() => setIconPickerFor(null)} testId="icon-picker-modal">
          <IconPicker
            value={iconPickerFor === "routine" ? icon : steps[iconPickerFor]?.icon}
            onChange={(name) => {
              if (iconPickerFor === "routine") setIcon(name);
              else updateStep(iconPickerFor, { icon: name, image: null });
              setIconPickerFor(null);
            }}
          />
        </Modal>
      )}
    </div>
  );
}
